/**
 * De vertaling voorlezen, in het tabblad LLM Config (stap 16): per moedertaal
 * aan of uit, met welke stem, en proefluisteren.
 *
 * Een leerling met een andere moedertaal laat een alinea vertalen; staat
 * voorlezen voor hem aan, dan kan hij die vertaling ook laten voorlezen. Dat
 * gaat altijd met Voxtral 3 (alleen dat model kent Turks, Pools, Oekraïens en
 * Chinees), in de talen die de server noemt (`voorlezen_moedertalen`).
 *
 * De stem per taal: een stem die Mistral in die taal heeft (de lijst haalt
 * dit scherm bij Mistral op), een eigen stem van een opname in die taal
 * (onderdelen/EigenStem.js), of leeg: de gewone voorleesstem, die de taal dan
 * met een accent spreekt. Luister een taal na met iemand die hem spreekt
 * voordat je hem aan laat staan; met het vinkje uit verdwijnt de knop voor
 * die taal.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Play, RefreshCw } from 'lucide-react';
import { beheerApi as api } from '../../../api/beheer';

/** De instellingen per taal als { taal: { aan, stem, stemnaam } }, zoals het formulier ze opslaat. */
export function moedertalenVan(config) {
  return Object.fromEntries((config.voorlezen_moedertalen || []).map(m => [m.taal, { aan: m.aan !== false, stem: m.stem || '', stemnaam: m.stemnaam || '' }]));
}

/** De naam van de stem voor een taal, om te tonen. */
export function stemVoorTaal(instelling, gewoneStemNaam) {
  if (!instelling || !instelling.stem) return `de gewone voorleesstem: ${gewoneStemNaam}`;
  return instelling.stemnaam || instelling.stem;
}

/** Alleen-lezen, in het overzicht. */
export function MoedertalenOverzicht({ config, gewoneStemNaam }) {
  const talen = config.voorlezen_moedertalen || [];
  if (!talen.length) return null;
  return (
    <div className="form-group mt-16">
      <label>De vertaling voorlezen ({config.voorleesmodel_moedertaal?.naam || 'Voxtral 3'})</label>
      <table style={{ fontSize: 13 }}>
        <tbody>
          {talen.map(m => (
            <tr key={m.taal}>
              <td>{m.taal}</td>
              <td>{m.aan !== false ? <span className="badge badge-success">aan</span> : <span className="badge badge-warning">uit</span>}</td>
              <td className="text-muted">{stemVoorTaal(m, gewoneStemNaam)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * In het formulier: per taal een vinkje, een stem en Luister. `waarde` is
 * { taal: { aan, stem, stemnaam } }; `onChange(taal, nieuweInstelling)`.
 */
export function MoedertaalStemmen({ config, waarde, onChange, gewoneStem, gewoneStemNaam }) {
  const talen = config.voorlezen_moedertalen || [];
  const [mistral, setMistral] = useState(null);       // de stemmen van Mistral, of null zolang ze er niet zijn
  const [mistralFout, setMistralFout] = useState('');
  const [luistert, setLuistert] = useState(null);     // de taal waarvan de proef onderweg is
  const [geluid, setGeluid] = useState(null);         // { taal, url }
  const [fout, setFout] = useState('');
  const huidigGeluid = useRef(null);

  useEffect(() => {
    api.haalMistralStemmen()
      .then(antwoord => setMistral(antwoord.stemmen || []))
      .catch(err => setMistralFout(err.message));
  }, []);
  useEffect(() => () => { if (huidigGeluid.current) URL.revokeObjectURL(huidigGeluid.current); }, []);

  if (!talen.length) return null;
  const eigen = config.eigen_stemmen || [];
  const model = config.voorleesmodel_moedertaal?.id;

  const zet = (taal, veranderd) => onChange(taal, { ...(waarde[taal] || { aan: true, stem: '', stemnaam: '' }), ...veranderd });

  const kiesStem = (taal, id) => {
    const gekozen = eigen.find(s => s.id === id) || (mistral || []).find(s => s.id === id);
    zet(taal, { stem: id, stemnaam: id ? (gekozen ? gekozen.naam : (waarde[taal]?.stemnaam || id)) : '' });
  };

  const luister = async (m) => {
    setLuistert(m.taal);
    setFout('');
    try {
      const stem = waarde[m.taal]?.stem || gewoneStem;
      const blob = await api.proefluister(stem, m.voorbeeld, 'gewoon', model, m.taal);
      if (huidigGeluid.current) URL.revokeObjectURL(huidigGeluid.current);
      huidigGeluid.current = URL.createObjectURL(blob);
      setGeluid({ taal: m.taal, url: huidigGeluid.current });
    } catch (err) {
      setFout(`Fout bij het ${m.taal}: ${err.message}`);
    } finally {
      setLuistert(null);
    }
  };

  return (
    <div className="mt-16">
      <h4 className="mb-8">De vertaling voorlezen, in de moedertaal</h4>
      <p className="text-muted text-small mb-8">
        Een leerling bij wie voorlezen aanstaat, kan de vertaling van een alinea ook laten voorlezen. Dat gaat altijd
        met {config.voorleesmodel_moedertaal?.naam || 'Voxtral 3'}, ook als hierboven een ander model staat. Elke stem
        spreekt elke taal, met een accent; een stem in de taal zelf klinkt beter. Laat iemand die de taal spreekt
        luisteren voordat je een taal aan laat staan.
      </p>
      {mistralFout && <div className="alert alert-error mb-8">Kon de stemmen van Mistral niet ophalen: {mistralFout}. Je kunt wel de gewone stem of een eigen stem kiezen.</div>}
      {fout && <div className="alert alert-error mb-8">{fout}</div>}
      <table style={{ fontSize: 13 }}>
        <tbody>
          {talen.map(m => {
            const instelling = waarde[m.taal] || { aan: true, stem: '', stemnaam: '' };
            const inTaal = (mistral || []).filter(s => (s.talen || []).includes(m.code));
            const eigenInTaal = eigen.filter(s => s.taal === m.taal);
            const bekend = !instelling.stem || eigen.some(s => s.id === instelling.stem)
              || (mistral || []).some(s => s.id === instelling.stem);
            return (
              <tr key={m.taal}>
                <td style={{ whiteSpace: 'nowrap' }}>
                  <label style={{ display: 'flex', gap: 6, alignItems: 'center', margin: 0 }}>
                    <input type="checkbox" checked={instelling.aan} onChange={e => zet(m.taal, { aan: e.target.checked })}
                      aria-label={`Vertaling voorlezen in het ${m.taal}`} />
                    {m.taal}
                  </label>
                </td>
                <td>
                  <select className="input" style={{ fontSize: 13, padding: '4px 6px' }} value={instelling.stem}
                    onChange={e => kiesStem(m.taal, e.target.value)} aria-label={`Stem voor het ${m.taal}`}>
                    <option value="">De gewone voorleesstem: {gewoneStemNaam}</option>
                    {!bekend && <option value={instelling.stem}>{instelling.stemnaam || instelling.stem}</option>}
                    {eigenInTaal.length > 0 && (
                      <optgroup label={`Eigen stemmen in het ${m.taal}`}>
                        {eigenInTaal.map(s => <option key={s.id} value={s.id}>{s.naam}</option>)}
                      </optgroup>
                    )}
                    {inTaal.length > 0 && (
                      <optgroup label={`Stemmen van Mistral in het ${m.taal}`}>
                        {inTaal.map(s => <option key={s.id} value={s.id}>{s.naam}</option>)}
                      </optgroup>
                    )}
                    {eigen.some(s => s.taal !== m.taal) && (
                      <optgroup label="Andere eigen stemmen">
                        {eigen.filter(s => s.taal !== m.taal).map(s => <option key={s.id} value={s.id}>{s.naam}</option>)}
                      </optgroup>
                    )}
                    {(mistral || []).some(s => !(s.talen || []).includes(m.code)) && (
                      <optgroup label="Andere stemmen van Mistral">
                        {mistral.filter(s => !(s.talen || []).includes(m.code)).map(s => (
                          <option key={s.id} value={s.id}>{s.naam}{s.talen?.length ? ` (${s.talen.join(', ')})` : ''}</option>
                        ))}
                      </optgroup>
                    )}
                  </select>
                </td>
                <td>
                  <button type="button" className="btn btn-secondary" style={{ padding: '4px 8px' }}
                    onClick={() => luister(m)} disabled={luistert !== null} title={`Luister: ${m.voorbeeld}`}
                    aria-label={`Luister in het ${m.taal}`}>
                    {luistert === m.taal ? <RefreshCw size={14} className="spinning" /> : <Play size={14} />}
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {geluid && (
        <div className="mt-8">
          <span className="text-muted text-small">{geluid.taal}:</span>
          <audio src={geluid.url} controls autoPlay style={{ display: 'block', width: '100%' }} data-testid="proefgeluid-moedertaal" />
        </div>
      )}
    </div>
  );
}
